import React, {
  useCallback, useEffect, useMemo, useState,
} from 'react';

import BottomSheet from 'components/DesignSystem/BottomSheet';
import Icon from 'components/DesignSystem/Icon';
import Snackbar from 'components/DesignSystem/Snackbar';
import SheetActionButton from 'components/Layout/MobileShell/SheetActionButton';
import { CustomAlert } from 'components/AlertsCollection';
import useGroupForm from 'hooks/LocationForm/RegisterForm/useGroupForm';
import useLocationForm from 'hooks/LocationForm/RegisterForm/useLocationForm';
import Group from 'types/Locations/Group';
import { GetCities } from 'types/Meteorology/Agro/getCities';
import { cssVar } from 'constants/theme';

import OptionCard from './OptionCard';
import OptionRow from '../OptionRow';

import './index.scss';

type WizardStep = 'tipo' | 'grupo' | 'nome' | 'local' | 'cidade' | 'coordenadas';
type WizardTipo = 'grupo' | 'local' | undefined;

const STEP_TITLES: Record<WizardStep, string> = {
  tipo: 'Novo cadastro',
  grupo: 'Grupo',
  nome: 'Nome do grupo',
  local: 'Local',
  cidade: 'Cidade',
  coordenadas: 'Coordenadas',
};

function getStepPosition(step: WizardStep, tipo: WizardTipo) {
  if (tipo === 'grupo') return step === 'tipo' ? 1 : 2;
  switch (step) {
    case 'tipo': return 1;
    case 'grupo': return 2;
    case 'local': return 3;
    default: return 4;
  }
}

export interface RegisterSheetProps {
  open: boolean;
  dismiss: () => void;
  idPrefix: string;
  alert: CustomAlert | undefined;
  groups: Group[] | undefined;
  cities: GetCities[] | undefined;
  saveGroup: any;
  saveLocationParams: any;
}

export default function RegisterSheet({
  open,
  dismiss,
  idPrefix,
  alert,
  groups,
  cities,
  saveGroup,
  saveLocationParams,
}: RegisterSheetProps) {
  const [tipo, setTipo] = useState<WizardTipo>(undefined);
  const [step, setStep] = useState<WizardStep>('tipo');
  const [continuation, setContinuation] = useState(false);
  const [citySearch, setCitySearch] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [saveSuccessful, setSaveSuccessful] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const {
    newGroup,
    setNewGroup,
    selectedGroup,
    setSelectedGroup,
    updateNewGroupField,
  } = useGroupForm();

  const {
    method,
    setMethod,
    city,
    setCity,
    changeNameField,
    locationName,
    setLocationName,
    changeLatField,
    lat,
    setLat,
    changeLonField,
    lon,
    setLon,
    isLatLonMethod,
    isPredefMethod,
  } = useLocationForm() as any;

  const resetAll = useCallback(() => {
    setTipo(undefined);
    setStep('tipo');
    setContinuation(false);
    setCitySearch('');
    setIsSaving(false);
    setSaveSuccessful(false);
    setShowAlert(false);
    setNewGroup('');
    setSelectedGroup(undefined);
    setMethod(undefined);
    setCity('');
    setLocationName('');
    setLat('');
    setLon('');
  }, [setNewGroup, setSelectedGroup, setMethod, setCity, setLocationName, setLat, setLon]);

  const handleClose = useCallback(() => {
    resetAll();
    dismiss();
  }, [resetAll, dismiss]);

  useEffect(() => {
    const newlyAddedGroup = groups?.find((g) => g.name === newGroup);
    if (newlyAddedGroup) setSelectedGroup(newlyAddedGroup.id);
  }, [groups, newGroup, setSelectedGroup]);

  useEffect(() => {
    if (alert) {
      if (alert.variant === 'success') {
        setSaveSuccessful(true);
        setIsSaving(false);
      } else {
        setShowAlert(true);
        setIsSaving(false);
      }
    }
  }, [alert]);

  const filteredCities = useMemo(() => {
    if (!cities) return [];
    const q = citySearch.trim().toLowerCase();
    if (!q) return cities;
    return cities.filter((c) => c.label.toLowerCase().includes(q));
  }, [cities, citySearch]);

  const isLastStep = step === 'nome' || step === 'cidade' || step === 'coordenadas';
  const showBack = step !== 'tipo' && !(continuation && (step === 'nome' || step === 'local'));

  const goNext = () => {
    if (step === 'tipo') {
      setStep(tipo === 'grupo' ? 'nome' : 'grupo');
      return;
    }
    if (step === 'grupo') {
      setStep('local');
      return;
    }
    if (step === 'local') {
      setStep(isLatLonMethod ? 'coordenadas' : 'cidade');
    }
  };

  const goBack = () => {
    if (step === 'nome' || step === 'grupo') {
      setStep('tipo');
      return;
    }
    if (step === 'local') {
      setStep('grupo');
      return;
    }
    if (step === 'cidade' || step === 'coordenadas') {
      setStep('local');
    }
  };

  const nextDisabled = useMemo(() => {
    if (step === 'tipo') return !tipo;
    if (step === 'grupo') return !selectedGroup;
    if (step === 'local') return !method;
    if (step === 'nome') return !(newGroup.length >= 3 && newGroup.length <= 30);
    if (step === 'cidade') return !city;
    if (step === 'coordenadas') return !(locationName && lat && lon);
    return true;
  }, [step, tipo, selectedGroup, method, newGroup, city, locationName, lat, lon]);

  let nextLabel = 'Próximo';
  if (isLastStep) {
    nextLabel = tipo === 'grupo' ? 'Salvar grupo' : 'Salvar local';
  }

  const onFooterClick = () => {
    if (!isLastStep) {
      goNext();
      return;
    }
    if (tipo === 'grupo') {
      saveGroup(newGroup, '');
    } else {
      saveLocationParams({
        group: selectedGroup, locationName, method, lat, lon, city,
      });
    }
    setIsSaving(true);
  };

  const onContinueLocation = () => {
    setIsSaving(false);
    setSaveSuccessful(false);
    setShowAlert(false);
    setCity('');
    setLocationName('');
    setLat('');
    setLon('');
    setCitySearch('');
    setMethod(undefined);
    setTipo('local');
    setContinuation(true);
    setStep('local');
  };

  const onContinueGroup = () => {
    setIsSaving(false);
    setSaveSuccessful(false);
    setShowAlert(false);
    setNewGroup('');
    setSelectedGroup(undefined);
    setTipo('grupo');
    setContinuation(true);
    setStep('nome');
  };

  if (saveSuccessful) {
    return (
      <BottomSheet open={open} dismiss={handleClose} title="Cadastro realizado com sucesso">
        <div className="register-sheet-step">
          {tipo === 'local' && (
            <div className="register-sheet-notice">
              Os dados serão processados para esse novo local e estarão disponíveis a partir das
              18h do dia seguinte.
            </div>
          )}
          <div className="register-sheet-success-actions">
            <SheetActionButton variant="secondary" onClick={onContinueLocation}>
              {tipo === 'grupo' ? 'Cadastrar novo local para este grupo' : 'Cadastrar novo local'}
            </SheetActionButton>
            <SheetActionButton variant="secondary" onClick={onContinueGroup}>
              Cadastrar novo grupo
            </SheetActionButton>
          </div>
          <SheetActionButton variant="confirm" onClick={handleClose}>
            Fechar
          </SheetActionButton>
        </div>
      </BottomSheet>
    );
  }

  return (
    <BottomSheet open={open} dismiss={handleClose} title={STEP_TITLES[step]}>
      {step !== 'tipo' && (
        <div className="register-sheet-progress">
          {showBack ? (
            <button type="button" aria-label="Voltar" className="register-sheet-back" onClick={goBack}>
              <Icon iconType="left_arrow" color={cssVar('text-strong')} size="small" />
            </button>
          ) : <span />}
          <div className="register-sheet-dots">
            {Array.from({ length: tipo === 'grupo' ? 2 : 4 }).map((_, i) => (
              // eslint-disable-next-line react/no-array-index-key
              <span key={i} className={`register-sheet-dot${i < getStepPosition(step, tipo) ? ' filled' : ''}`} />
            ))}
          </div>
        </div>
      )}

      {showAlert && alert && (
        <Snackbar variant={alert.variant} className="mb-3">
          {alert.title && <div className="mb-1"><b>{alert.title}</b></div>}
          <div>{alert.message}</div>
        </Snackbar>
      )}

      {step === 'tipo' && (
        <div className="register-sheet-step">
          <OptionCard
            label="Grupo"
            description="Conjunto de locais"
            selected={tipo === 'grupo'}
            onSelect={() => setTipo('grupo')}
          />
          <OptionCard
            label="Local"
            description="Ponto/cidade individual"
            selected={tipo === 'local'}
            onSelect={() => setTipo('local')}
          />
        </div>
      )}

      {step === 'nome' && (
        <div className="register-sheet-step">
          <label className="register-sheet-label" htmlFor={`${idPrefix}-new-group-name`}>
            <span>Nome do grupo</span>
            <input
              id={`${idPrefix}-new-group-name`}
              className="register-sheet-input"
              value={newGroup}
              onChange={updateNewGroupField}
              placeholder="Ex.: Fazendas Sul"
            />
          </label>
          <span className="register-sheet-hint">{`${newGroup.length}/30`}</span>
        </div>
      )}

      {step === 'grupo' && (
        <div className="register-sheet-step register-sheet-step-list">
          {groups?.map((g) => (
            <OptionRow
              key={g.id}
              label={g.name}
              selected={selectedGroup === g.id}
              onSelect={() => setSelectedGroup(g.id)}
            />
          ))}
          {!groups?.length && (
            <p className="register-sheet-empty">Nenhum grupo cadastrado. Crie um grupo primeiro.</p>
          )}
        </div>
      )}

      {step === 'local' && (
        <div className="register-sheet-step">
          <OptionCard
            label="Locais pré-definidos"
            description="Catálogo de cidades Ampere"
            selected={isPredefMethod}
            onSelect={() => setMethod('predef')}
          />
          <OptionCard
            label="Coordenadas (lat/lon)"
            description="Informe latitude e longitude"
            selected={isLatLonMethod}
            onSelect={() => setMethod('latlon')}
          />
        </div>
      )}

      {step === 'cidade' && (
        <div className="register-sheet-step register-sheet-step-list">
          <div className="register-sheet-search">
            <Icon iconType="search" color={cssVar('text-muted')} size="small" />
            <input
              className="register-sheet-search-input"
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              placeholder="Buscar cidade"
            />
          </div>
          <div className="register-sheet-step-list-scroll">
            {filteredCities.map((c) => (
              <OptionRow
                key={c.id}
                label={c.label}
                sublabel={c.state}
                selected={city === c.id}
                onSelect={() => setCity(c.id)}
              />
            ))}
            {!filteredCities.length && (
              <p className="register-sheet-empty">Nenhuma cidade encontrada.</p>
            )}
          </div>
        </div>
      )}

      {step === 'coordenadas' && (
        <div className="register-sheet-step">
          <label className="register-sheet-label" htmlFor={`${idPrefix}-loc-name`}>
            <span>Nome do local</span>
            <input
              id={`${idPrefix}-loc-name`}
              className="register-sheet-input"
              value={locationName}
              onChange={changeNameField}
              placeholder="Ex.: Sítio Boa Vista"
            />
          </label>
          <div className="register-sheet-row">
            <div className="register-sheet-col">
              <label className="register-sheet-label" htmlFor={`${idPrefix}-loc-lat`}>
                <span>Latitude</span>
                <input
                  id={`${idPrefix}-loc-lat`}
                  className="register-sheet-input"
                  value={lat}
                  onChange={changeLatField}
                  placeholder="-15.79"
                />
              </label>
            </div>
            <div className="register-sheet-col">
              <label className="register-sheet-label" htmlFor={`${idPrefix}-loc-lon`}>
                <span>Longitude</span>
                <input
                  id={`${idPrefix}-loc-lon`}
                  className="register-sheet-input"
                  value={lon}
                  onChange={changeLonField}
                  placeholder="-47.88"
                />
              </label>
            </div>
          </div>
        </div>
      )}

      <div className="register-sheet-footer">
        <SheetActionButton
          variant="confirm"
          disabled={nextDisabled || isSaving}
          loading={isSaving}
          onClick={onFooterClick}
        >
          {nextLabel}
        </SheetActionButton>
      </div>
    </BottomSheet>
  );
}
